<script setup>
import { computed, defineModel } from 'vue';
import { useI18n } from 'vue-i18n';

import SearchDateRangeSelector from 'dashboard/modules/search/components/SearchDateRangeSelector.vue';
import SearchContactAgentSelector from 'dashboard/modules/search/components/SearchContactAgentSelector.vue';
import SearchInboxSelector from 'dashboard/modules/search/components/SearchInboxSelector.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import MonitorStatusSelector from './MonitorStatusSelector.vue';

const emit = defineEmits(['change', 'reset']);
const { t } = useI18n();

const filters = defineModel({
  type: Object,
  default: () => ({
    status: 'all',
    agent: null,
    inboxId: null,
    dateRange: { type: null, from: null, to: null },
  }),
});

const hasActiveFilters = computed(
  () =>
    filters.value.status !== 'all' ||
    filters.value.agent ||
    filters.value.inboxId ||
    filters.value.dateRange?.type ||
    filters.value.dateRange?.from ||
    filters.value.dateRange?.to
);

const activeFilterCount = computed(() => {
  let count = 0;
  if (filters.value.status !== 'all') count += 1;
  if (filters.value.agent) count += 1;
  if (filters.value.inboxId) count += 1;
  if (
    filters.value.dateRange?.type ||
    filters.value.dateRange?.from ||
    filters.value.dateRange?.to
  ) {
    count += 1;
  }
  return count;
});

const onChange = () => emit('change', filters.value);

const resetFilters = () => {
  filters.value = {
    status: 'all',
    agent: null,
    inboxId: null,
    dateRange: { type: null, from: null, to: null },
  };
  emit('reset');
};
</script>

<template>
  <div
    class="chatmigo-monitor-filters relative z-20 flex flex-col gap-3 border-b border-n-strong/60 bg-n-surface-1 px-3 py-3"
  >
    <div class="flex flex-wrap items-start justify-between gap-3">
      <div class="min-w-0">
        <div class="flex items-center gap-2">
          <span
            class="inline-flex size-7 items-center justify-center rounded-xl border border-n-strong/70 bg-n-alpha-1 text-n-slate-11"
          >
            <fluent-icon icon="filter" size="14" />
          </span>
          <div>
            <div class="text-sm font-semibold tracking-tight text-n-slate-12">
              {{ t('MONITOR_PAGE.FILTERS.TITLE') }}
            </div>
            <p class="m-0 text-xs text-n-slate-10">
              {{
                hasActiveFilters
                  ? t('MONITOR_PAGE.SEARCH.FILTERED_HINT')
                  : t('MONITOR_PAGE.SEARCH.DEFAULT_HINT')
              }}
            </p>
          </div>
        </div>
      </div>

      <div class="flex items-center gap-2">
        <span
          v-if="hasActiveFilters"
          class="rounded-full border border-n-brand/20 bg-n-brand/10 px-2 py-1 text-[11px] font-semibold text-n-brand"
        >
          {{ activeFilterCount }}
        </span>
        <Button
          v-if="hasActiveFilters"
          sm
          slate
          faded
          :label="t('MONITOR_PAGE.FILTERS.CLEAR')"
          icon="i-lucide-circle-x"
          @click="resetFilters"
        />
      </div>
    </div>

    <div class="grid grid-cols-1 gap-2 sm:grid-cols-2">
      <div class="chatmigo-monitor-filter-control">
        <MonitorStatusSelector
          v-model="filters.status"
          :label="t('MONITOR_PAGE.FILTERS.STATUS')"
          @change="onChange"
        />
      </div>

      <div class="chatmigo-monitor-filter-control">
        <SearchInboxSelector
          v-model="filters.inboxId"
          :label="t('MONITOR_PAGE.FILTERS.INBOX')"
          @change="onChange"
        />
      </div>

      <div class="chatmigo-monitor-filter-control">
        <SearchContactAgentSelector
          v-model="filters.agent"
          mode="agent"
          :label="t('MONITOR_PAGE.FILTERS.AGENT')"
          @change="onChange"
        />
      </div>

      <div class="chatmigo-monitor-filter-control">
        <SearchDateRangeSelector
          v-model="filters.dateRange"
          @change="onChange"
        />
      </div>
    </div>
  </div>
</template>
